// Display formatting shared by the screens, matching the iOS strings (GroundTeam/Models/CompanySettings.swift money,
// GroundTeam/Views/Components/Formatting.swift, Geo.formatShortDistance). Locale-dependent output uses the browser's
// locale unless a `locale` is passed (iOS uses the device locale the same way).

import { formatDistance, type DistanceUnit } from '@/contracts/enums';
import { calendarDaysBetween, isSameDay, startOfDayOffset } from './calendar';

export { formatDistance };

function numberFormat(locale: string | undefined, options: Intl.NumberFormatOptions): Intl.NumberFormat | null {
  try {
    return new Intl.NumberFormat(locale, options);
  } catch {
    return null;
  }
}

/** A finite number, or 0 (NaN/Infinity never reach the screen). */
function finite(v: number): number {
  return Number.isFinite(v) ? v : 0;
}

/**
 * Swift `String(format: "%.<digits>f", x)` (C printf). Same digits as `toFixed`, except on an exact tie: printf
 * rounds it to the even digit ("%.0f" of 12.5 is "12", "%.1f" of 1.25 is "1.2") where `toFixed` rounds away from
 * zero ("13", "1.3"). Ties are common in screens: $12,500 in a KPI tile is 12.5 thousand.
 */
export function formatFixed(x: number, digits: number): string {
  const js = x.toFixed(digits);
  if (!Number.isFinite(x) || Math.abs(x) >= 1e21) return js;
  // The exact decimal expansion of the double (100 places hold it for every |x| ≥ 2^-48).
  const exact = Math.abs(x).toFixed(100);
  const dot = exact.indexOf('.');
  const cut = digits === 0 ? dot : dot + 1 + digits;
  if (!/^50*$/.test(exact.slice(dot + 1 + digits))) return js;
  const kept = exact.slice(0, cut);
  // A tie: toFixed took the larger magnitude. printf keeps the smaller one when its last digit is even.
  if (Number(kept[kept.length - 1]) % 2 === 1) return js;
  return (x < 0 ? '-' : '') + kept;
}

/**
 * `CompanySettings.currencySymbol`: "$" for USD, "€" for EUR…, the code itself when the locale has no symbol for it.
 */
export function currencySymbol(currencyCode: string, locale?: string): string {
  const f = numberFormat(locale, { style: 'currency', currency: currencyCode || 'USD' });
  const symbol = f?.formatToParts(0).find((p) => p.type === 'currency')?.value ?? '$';
  return symbol === '' ? currencyCode : symbol;
}

/**
 * `CompanySettings.money(_:)`: whole amounts without decimals ("$900"), others with two ("$1,250.50"), in the company
 * currency (`settings.currencyCode`).
 */
export function formatMoney(value: number, currencyCode: string, locale?: string): string {
  const v = finite(value);
  const integral = v === Math.trunc(v);
  const base = numberFormat(locale, { style: 'currency', currency: currencyCode || 'USD' });
  if (!base) return `${currencyCode} ${Math.round(v).toLocaleString(locale)}`;
  if (integral) {
    return numberFormat(locale, {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })!.format(v);
  }
  const min = Math.min(2, base.resolvedOptions().minimumFractionDigits ?? 2);
  // NumberFormatter rounds half to even ($0.125 → "$0.12"); Intl's default would give "$0.13".
  return numberFormat(locale, {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: min,
    maximumFractionDigits: 2,
    roundingMode: 'halfEven',
  })!.format(v);
}

/** `asCompactCurrency`: "$1.2K", "$34K", "$3.4M", "$1.2B" for KPI tiles; small amounts as `formatMoney`. */
export function formatCompactMoney(value: number, currencyCode: string, locale?: string): string {
  const v = finite(value);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const symbol = currencySymbol(currencyCode, locale);
  if (abs >= 1_000_000_000) return `${sign}${symbol}${formatFixed(abs / 1_000_000_000, 1)}B`;
  if (abs >= 1_000_000) return `${sign}${symbol}${formatFixed(abs / 1_000_000, 1)}M`;
  if (abs >= 10_000) return `${sign}${symbol}${formatFixed(abs / 1_000, 0)}K`;
  if (abs >= 1_000) return `${sign}${symbol}${formatFixed(abs / 1_000, 1)}K`;
  return formatMoney(v, currencyCode, locale);
}

/** `Int.grouped`: "1,234". */
export function formatGrouped(n: number, locale?: string): string {
  return numberFormat(locale, { maximumFractionDigits: 0 })?.format(finite(n)) ?? String(Math.round(finite(n)));
}

/**
 * A 0…1 ratio as "42%": `ReportFormat.percent` / the "Verified %" column, `String(format: "%.0f%%", ratio * 100)`.
 * Not localized and printf-rounded, as on iOS (1 of 8 is "12%", not "13%").
 */
export function formatPercent(ratio: number, fractionDigits = 0): string {
  return `${formatFixed(finite(ratio) * 100, fractionDigits)}%`;
}

/** `Geo.formatShortDistance(meters:unit:)`: "262 ft" / "1.2 mi", or "80 m" / "1.2 km". */
export function formatShortDistance(meters: number, unit: DistanceUnit): string {
  const safe = Number.isFinite(meters) ? Math.max(0, meters) : 0;
  if (unit === 'kilometers') return safe < 1000 ? `${Math.round(safe)} m` : `${formatFixed(safe / 1000, 1)} km`;
  const feet = safe * 3.28084;
  return feet < 1000 ? `${Math.round(feet)} ft` : `${formatFixed(feet / 5280, 1)} mi`;
}

/** Miles or kilometers as a number in the company unit (tables, "Miles/Km" columns). */
export function distanceInUnit(meters: number, unit: DistanceUnit): number {
  return unit === 'miles' ? meters / 1609.344 : meters / 1000;
}

const RELATIVE_STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.348],
  ['month', 12],
  ['year', Infinity],
];

/**
 * `Date.relativeDescription`: "Just now" within a minute, else "6 minutes ago" / "in 2 hours" / "3 days ago"
 * (numeric, full units, the largest unit that fits, like RelativeDateTimeFormatter).
 */
export function formatRelative(date: Date, now: Date = new Date(), locale?: string): string {
  const deltaSec = (date.getTime() - now.getTime()) / 1000;
  if (Math.abs(deltaSec) < 60) return 'Just now';
  let value = deltaSec;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const [u, size] of RELATIVE_STEPS) {
    unit = u;
    if (Math.abs(value) < size) break;
    value /= size;
  }
  const rounded = Math.trunc(value);
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'long' }).format(rounded, unit);
  } catch {
    return `${Math.abs(rounded)} ${unit}${Math.abs(rounded) === 1 ? '' : 's'} ${rounded < 0 ? 'ago' : 'from now'}`;
  }
}

/**
 * `formatRelative` for things that already happened (last seen, last position, last activity): a time slightly in
 * the future — a phone clock running fast, or sample data — reads "Just now", never "in 4 minutes".
 */
export function formatElapsed(date: Date, now: Date = new Date(), locale?: string): string {
  return formatRelative(date.getTime() > now.getTime() ? now : date, now, locale);
}

function dateFormat(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    return new Intl.DateTimeFormat(undefined, options);
  }
}

/** `Date.shortDate`: "Sep 22, 2026". */
export function formatShortDate(date: Date, locale?: string): string {
  return dateFormat(locale, { dateStyle: 'medium' }).format(date);
}

/** `Date.shortTime`: "2:43 PM". */
export function formatShortTime(date: Date, locale?: string): string {
  return dateFormat(locale, { timeStyle: 'short' }).format(date);
}

/** `Date.shortDateTime`: "Sep 22, 2:43 PM", with the year when it is not this year. */
export function formatShortDateTime(date: Date, now: Date = new Date(), locale?: string): string {
  const sameYear = date.getFullYear() === now.getFullYear();
  return dateFormat(locale, {
    year: sameYear ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** `Date.dayDescription`: "Today", "Yesterday", "Tomorrow" or the short date. */
export function formatDayDescription(date: Date, now: Date = new Date(), locale?: string): string {
  if (isSameDay(date, now)) return 'Today';
  if (isSameDay(date, startOfDayOffset(now, -1))) return 'Yesterday';
  if (isSameDay(date, startOfDayOffset(now, 1))) return 'Tomorrow';
  return formatShortDate(date, locale);
}

/** `daysSinceText`: "Never visited", "Visited today", "1 day from last visit", "12 days from last visit". */
export function daysSinceText(lastVisitAt: Date | null | undefined, now: Date = new Date()): string {
  if (!lastVisitAt) return 'Never visited';
  const days = Math.max(0, calendarDaysBetween(lastVisitAt, now));
  if (days === 0) return 'Visited today';
  if (days === 1) return '1 day from last visit';
  return `${days} days from last visit`;
}

/** `visitCountText`: "No visits", "1 visit", "3 visits". */
export function visitCountText(count: number): string {
  if (count === 0) return 'No visits';
  if (count === 1) return '1 visit';
  return `${count} visits`;
}
